import { useState } from "react";
import { X, Check, ChevronRight } from "lucide-react";

const domeinen = [
  {
    key: "zorg",
    label: "Zorg",
    lede: "Waarom LevelLeven óók zorg is.",
    tekst:
      "Veel deelnemers hebben op meerdere leefgebieden zorgcontact, GGZ, huisarts, verslavingszorg. LevelLeven vervangt geen zorg, maar herstelt het fundament (dagritme, veiligheid, verbondenheid) waarop zorg vaak strandt. Voor de zorgketen betekent dat: minder terugval, minder no-shows, en een gedragskundige basis voor vervolgbehandeling.",
    bullets: [
      "Complementair aan GGZ en huisartsenzorg",
      "Vermindert zorgconsumptie doordat dagstructuur en zelfregie terugkeren",
      "Warme overdracht naar zorgpartners waar nodig",
    ],
  },
  {
    key: "welzijn",
    label: "Welzijn",
    lede: "Waarom LevelLeven óók welzijn is.",
    tekst:
      "Welzijnswerk in de wijk werkt aan verbinding en veerkracht. LevelLeven biedt daaraan een gestructureerde twaalfweekse ontwikkelomgeving: een vaste groep, twee trainers, buddy's en een warme lunch. Wat welzijn opbouwt in de wijk, versterken wij tot duurzame verandering, niet als losstaand traject, maar in samenwerking met de lokale welzijnspartner.",
    bullets: [
      "Vaste groep in de wijk, lokale welzijnspartner is co-uitvoerder",
      "Buddy-systeem vergroot sociale veerkracht",
      "80% oefening en ervaring, geen therapie, wel groei",
    ],
  },
  {
    key: "participatie",
    label: "Participatie",
    lede: "Waarom LevelLeven óók participatie is.",
    tekst:
      "Participatie begint niet bij een baan, het begint bij ergens bij horen. LevelLeven activeert deelnemers eerst maatschappelijk: wijkactiviteiten, vrijwilligerswerk, mantelzorg, dagbesteding. Pas daarna volgt de stap naar werk of opleiding. Zo sluit het programma naadloos aan op de Participatiewet in Balans, met duurzame regie als doel.",
    bullets: [
      "Volledig inpasbaar in het participatiebudget",
      "Gebouwd op de nieuwe koers 'Participatiewet in Balans'",
      "Van dagstructuur → wijkdeelname → opleiding of werk",
    ],
  },
  {
    key: "gezondheid",
    label: "Gezondheid",
    lede: "Waarom LevelLeven óók gezondheid is.",
    tekst:
      "Voeding, beweging, slaap en stress vormen samen het lichamelijke fundament waarop gedragsverandering rust. LevelLeven neemt gezondheid expliciet mee als onderdeel van het programma, niet als leefstijl-training, maar als levensvoorwaarde. Preventief: het voorkomt zwaardere zorgkosten in de toekomst.",
    bullets: [
      "Gezondheid als basis, niet als bijzaak",
      "Preventieve waarde, minder chronisch zorggebruik",
      "Aandacht voor mentale én fysieke conditie",
    ],
  },
  {
    key: "arbeid",
    label: "Arbeid",
    lede: "Waarom LevelLeven óók arbeid is.",
    tekst:
      "Deelnemers stromen uit naar vrijwilligerswerk, stage, opleiding, ondernemerschap of loondienst. LevelLeven koppelt aan maatschappelijk betrokken werkgevers en biedt een aanvullend trainingsprogramma voor werkgevers die deze mensen overnemen. Zo eindigt de reis niet bij uitstroom, maar bij duurzame plaatsing.",
    bullets: [
      "Koppeling aan maatschappelijk betrokken werkgevers",
      "Aparte werkgevers-training voor duurzame plaatsing",
      "Certificering die deelnemers houvast geeft",
    ],
  },
];

export default function IntegraalDomeinen() {
  const [active, setActive] = useState(null);
  const cur = domeinen.find((d) => d.key === active);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {domeinen.map((d) => {
          const isActive = active === d.key;
          return (
            <button
              key={d.key}
              type="button"
              data-testid={`domein-${d.key}`}
              onClick={() => setActive(isActive ? null : d.key)}
              aria-expanded={isActive}
              className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
                isActive
                  ? "bg-[#1D2A22] border-[#1D2A22] text-[#FAF7F1]"
                  : "border-[#E5E1D8] bg-[#FAF7F1] text-[#1D2A22] hover:border-[#1D2A22]"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  isActive ? "bg-[#D19B72]" : "bg-[#E06B3F]"
                }`}
              />
              {d.label}
              <ChevronRight
                size={14}
                className={`transition-transform ${isActive ? "rotate-90" : ""}`}
              />
            </button>
          );
        })}
      </div>

      {cur && (
        <div
          data-testid={`domein-detail-${cur.key}`}
          className="mt-6 rounded-2xl border border-[#E5E1D8] bg-[#FAF7F1] p-6 sm:p-8 animate-in fade-in duration-500"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-2">
                {cur.label}
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl text-[#1D2A22] leading-tight font-light">
                {cur.lede}
              </h3>
            </div>
            <button
              onClick={() => setActive(null)}
              aria-label="Sluit uitleg"
              className="shrink-0 text-[#7A8276] hover:text-[#1D2A22] transition-colors"
            >
              <X size={18} />
            </button>
          </div>
          <p className="mt-5 text-[#4A5D4E] leading-relaxed font-light">
            {cur.tekst}
          </p>
          <ul className="mt-6 space-y-2.5">
            {cur.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-[#1D2A22] text-sm">
                <Check size={16} className="mt-0.5 text-[#E06B3F] shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
